import { type Component, createMemo, type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { useTalentPool } from "@/domains/company/talent-pool/context/talent-pool-provider";
import { SaveFields } from "./save-fields";

export type SavePopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancerProfileId: string;
  /** Desktop-only anchor for the popover; forwarded to `ResponsivePopover`. */
  anchor?: JSX.Element | (() => Element);
};

/**
 * `SavePopover` — the card-side save-confirmation surface, built on the reusable
 * `ResponsivePopover` (anchored popover on desktop with NO backdrop, bottom
 * drawer on mobile). It wraps `SaveFields` with a ✓ header + subtitle and a
 * "Rimuovi" / "Fatto" footer.
 *
 * Dismissal persists a dirty note (D8). Every dismissal path — "Fatto", outside
 * click, Esc, drawer close — funnels through `handleOpenChange`, which flushes
 * the `SaveFields` commit BEFORE letting the close proceed. An untouched
 * dismissal commits nothing (the commit is gated by `SaveFields`' local dirty
 * flag). Visibility already persists on toggle inside `SaveFields`. "Rimuovi"
 * deletes the entry and closes without committing (the note is going away).
 */
export const SavePopover: Component<SavePopoverProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();

  // Set once when `SaveFields` mounts; reads the latest draft at call time.
  let commitNote: (() => void) | undefined;

  const entry = createMemo(() => talentPool.entry(props.freelancerProfileId));
  const isReadOnly = createMemo(() => entry()?.saved_by_me === false);

  const handleOpenChange = (next: boolean) => {
    // Persist a dirty note first, then let the close proceed (D8). Applies to
    // every dismissal path, since `ResponsivePopover` routes them all here.
    if (!next) commitNote?.();
    props.onOpenChange(next);
  };

  const handleRemove = () => {
    // No note commit — the entry is being deleted.
    void talentPool.remove(props.freelancerProfileId);
    props.onOpenChange(false);
  };

  return (
    <ResponsivePopover
      open={props.open}
      onOpenChange={handleOpenChange}
      anchor={props.anchor}
      title={t("company.talent_pool.popover.title")}
      hideCloseButton
      contentClass="w-80 p-5"
    >
      <div class="flex flex-col gap-5">
        <p class="font-semibold">{t("company.talent_pool.popover.title")}</p>

        <SaveFields
          freelancerProfileId={props.freelancerProfileId}
          registerCommit={(commit) => {
            commitNote = commit;
          }}
        />

        <Show when={!isReadOnly()}>
          <footer class="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              class="gap-1.5 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <i class="ph ph-trash text-base" aria-hidden="true" />
              {t("company.talent_pool.popover.footer.remove")}
            </Button>
            <Button size="sm" onClick={() => handleOpenChange(false)}>
              {t("company.talent_pool.popover.footer.done")}
            </Button>
          </footer>
        </Show>
      </div>
    </ResponsivePopover>
  );
};
